/**
 * Teacher Gradebook — scheme, evidence scores, Intervention Radar, CSV, LMS sync, Impact brief.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import ButtonBase from '@mui/material/ButtonBase';
import DownloadIcon from '@mui/icons-material/Download';
import SyncIcon from '@mui/icons-material/Sync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RadarIcon from '@mui/icons-material/TrackChanges';
import StudentAccommodationDialog from './StudentAccommodationDialog.js';
import HelpTip, { TEACHER_KPI_HELP } from './HelpTip.js';
import { getLearningDashboardUrl } from '../lib/learningDashboardUrl.js';
import { setImpersonationEnglishChrome } from '../lib/institutionLeaderHubSession.js';
import { downloadBlankCertificatePdf } from '../lib/commandlineCertificatesPdf.js';

function riskChip(level) {
  if (level === 'high') return { label: 'High', color: '#9a3412', bg: 'rgba(251,146,60,0.2)' };
  if (level === 'medium') return { label: 'Watch', color: '#854d0e', bg: 'rgba(250,204,21,0.18)' };
  return { label: 'OK', color: '#065f46', bg: 'rgba(16,185,129,0.14)' };
}

export default function TeacherGradebookPanel({
  sectionId,
  sectionName,
  sections = [],
  onReassigned,
  disabled = false,
  focusMode = null,
  onFocusHandled,
  panelRef = null,
  /** Owner support: show Impersonate on student rows (also when owner is already impersonating a teacher). */
  canImpersonateStudents = false
}) {
  const [tab, setTab] = useState(0);
  const [scoreFilter, setScoreFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [data, setData] = useState(null);
  const [schemeDraft, setSchemeDraft] = useState([]);
  const [schemeName, setSchemeName] = useState('Linux Phase 1');
  const [onTrackPct, setOnTrackPct] = useState(80);
  const [interventionMasteryPct, setInterventionMasteryPct] = useState(60);
  const [idleDaysThreshold, setIdleDaysThreshold] = useState(7);
  const [savingScheme, setSavingScheme] = useState(false);
  const [lmsPreview, setLmsPreview] = useState(null);
  const [lmsBusy, setLmsBusy] = useState(false);
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [reassignTarget, setReassignTarget] = useState({});
  const [resetAssessmentTarget, setResetAssessmentTarget] = useState({});
  const [resetAssessmentBusy, setResetAssessmentBusy] = useState(null);
  const [impersonateBusyId, setImpersonateBusyId] = useState(null);
  const [supportsStudent, setSupportsStudent] = useState(null);

  const applyGradebookPayload = useCallback((json) => {
    if (!json) {
      setData(null);
      return;
    }
    setData(json);
    setSchemeDraft(Array.isArray(json.scheme?.items) ? json.scheme.items.map((i) => ({ ...i })) : []);
    setSchemeName(json.scheme?.name || 'Linux Phase 1');
    setOnTrackPct(json.scheme?.onTrackPct ?? 80);
    setInterventionMasteryPct(json.scheme?.interventionMasteryPct ?? 60);
    setIdleDaysThreshold(json.scheme?.idleDaysThreshold ?? 7);
    const drafts = {};
    (json.students || []).forEach((s) => {
      drafts[s.userId] = s.overridePoints != null ? String(s.overridePoints) : '';
    });
    setOverrideDrafts(drafts);
  }, []);

  const load = useCallback(async () => {
    if (!sectionId || sectionId === 'all' || disabled) {
      setData(null);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/gradebook`, {
        credentials: 'include',
        headers: { Accept: 'application/json' }
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json.error ? String(json.error).replace(/_/g, ' ') : 'Could not load gradebook');
        setData(null);
        return;
      }
      applyGradebookPayload(json);
    } catch {
      setError('Network error loading gradebook');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [sectionId, disabled, applyGradebookPayload]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!focusMode) return;
    if (focusMode === 'on_track' || focusMode === 'needs_help') {
      setTab(0);
      setScoreFilter('on_track');
      setMessage(
        focusMode === 'on_track'
          ? 'Showing students on track for this class (scheme score vs class/student bar).'
          : 'Showing students who need help — open Intervention Radar for full risk detail.'
      );
      if (focusMode === 'needs_help') {
        setTab(1);
        setScoreFilter('all');
      }
    } else if (focusMode === 'radar') {
      setTab(1);
      setScoreFilter('all');
      setMessage('Intervention Radar for the selected class.');
    }
    if (panelRef?.current && typeof panelRef.current.scrollIntoView === 'function') {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    if (typeof onFocusHandled === 'function') onFocusHandled();
  }, [focusMode, onFocusHandled, panelRef]);

  useEffect(() => {
    if (!data || disabled) return undefined;
    if (window.__skyliteForceEnglishChrome) return undefined;
    const t = window.setTimeout(() => {
      if (typeof window.__skyliteRequestRetranslate === 'function') {
        window.__skyliteRequestRetranslate({ force: true });
      }
    }, 120);
    return () => window.clearTimeout(t);
  }, [data, tab, disabled]);

  const filteredStudents = useMemo(() => {
    const list = data?.students || [];
    if (scoreFilter === 'on_track') return list.filter((s) => s.onTrack);
    if (scoreFilter === 'behind') return list.filter((s) => !s.onTrack);
    return list;
  }, [data, scoreFilter]);

  const certificateEarners = useMemo(() => {
    const rows = [];
    (data?.students || []).forEach((s) => {
      (s.certificates || []).forEach((c) => {
        rows.push({
          userId: s.userId,
          name: s.name,
          email: s.email,
          id: c.id,
          level: c.level,
          title: c.title,
          subtitle: c.subtitle
        });
      });
    });
    return rows;
  }, [data]);

  const openKpi = (id) => {
    if (id === 'on_track') {
      setTab(0);
      setScoreFilter('on_track');
      setMessage('On-track students for this class.');
    } else if (id === 'radar' || id === 'needs_help') {
      setTab(1);
      setScoreFilter('all');
      setMessage('Intervention Radar for this class.');
    }
    if (panelRef?.current?.scrollIntoView) {
      panelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (!sectionId || sectionId === 'all') {
    return (
      <Paper sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #22d3ee' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.75 }}>
          CommandLine Gradebook
        </Typography>
        <Typography variant="body2" sx={{ color: '#94a3b8', lineHeight: 1.65 }}>
          Select a class to open the professional gradebook: teacher-controlled points, Intervention Radar,
          CSV export, LMS sync preview, and a grant-ready Impact brief — built on verified terminal practice.
        </Typography>
      </Paper>
    );
  }

  const downloadCsv = async () => {
    setError('');
    try {
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/grades.csv`, {
        credentials: 'include'
      });
      if (!r.ok) {
        setError('CSV export failed');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = String(sectionName || 'class')
        .replace(/[^\w\s-]+/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 40);
      a.download = `${slug || 'class'}-grades.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('Grades CSV downloaded.');
    } catch {
      setError('CSV download failed');
    }
  };

  const downloadCertificatesCsv = async () => {
    setError('');
    try {
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/certificates.csv`, {
        credentials: 'include'
      });
      if (!r.ok) {
        setError('Certificates CSV export failed');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const slug = String(sectionName || 'class')
        .replace(/[^\w\s-]+/g, '')
        .replace(/\s+/g, '-')
        .slice(0, 40);
      a.download = `${slug || 'class'}-certificates.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage('Certificates CSV downloaded.');
    } catch {
      setError('Certificates CSV download failed');
    }
  };

  const printBlankCertificate = (cert) => {
    setError('');
    try {
      downloadBlankCertificatePdf(cert);
      setMessage(`Certification PDF ready for ${cert?.title || 'certificate'} — name line left empty to write in.`);
    } catch (err) {
      console.error(err);
      setError('Could not create certification PDF');
    }
  };

  const saveScheme = async (useTemplate) => {
    setSavingScheme(true);
    setError('');
    setMessage('');
    try {
      const body = useTemplate
        ? {
            template: 'linux_phase1',
            name: 'Linux Phase 1',
            onTrackPct: Number(onTrackPct) || 80,
            interventionMasteryPct: Number(interventionMasteryPct) || 60,
            idleDaysThreshold: Number(idleDaysThreshold) || 7
          }
        : {
            name: schemeName,
            onTrackPct: Number(onTrackPct) || 80,
            interventionMasteryPct: Number(interventionMasteryPct) || 60,
            idleDaysThreshold: Number(idleDaysThreshold) || 7,
            items: schemeDraft.map((it) => ({
              itemKey: it.itemKey,
              label: it.label,
              points: Number(it.points) || 0,
              levelNumber: it.levelNumber
            }))
          };
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/grade-scheme`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json.error ? String(json.error).replace(/_/g, ' ') : 'Could not save scheme');
        return;
      }
      setMessage('Grade scheme saved. Scores recalculated from verified practice.');
      await load();
    } catch {
      setError('Network error saving scheme');
    } finally {
      setSavingScheme(false);
    }
  };

  const saveOverride = async (userId) => {
    const raw = overrideDrafts[userId];
    setError('');
    try {
      const clear = raw === '' || raw == null;
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/gradebook/${userId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(clear ? { clear: true } : { overridePoints: Number(raw) })
      });
      if (!r.ok) {
        setError('Could not save override');
        return;
      }
      setMessage(clear ? 'Override cleared.' : 'Teacher override saved.');
      await load();
    } catch {
      setError('Network error saving override');
    }
  };

  const resetAssessment = async (userId) => {
    const level = Number(resetAssessmentTarget[userId]);
    if (!Number.isFinite(level) || level <= 0) {
      setError('Choose an assessment to reset first.');
      return;
    }
    const catalog = data?.resettableAssessments || [];
    const meta = catalog.find((a) => Number(a.level) === level);
    const label = meta?.label || `Level ${level}`;
    if (
      !window.confirm(
        `Allow this student to retake ${label}?\n\nThis clears the pass for that assessment only (same idea as student Retry). Track certifications are not available here.`
      )
    ) {
      return;
    }
    setError('');
    setResetAssessmentBusy(userId);
    try {
      const r = await fetch(
        `/api/institutions/active/sections/${sectionId}/gradebook/${userId}/reset-assessment`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ assessmentLevel: level })
        }
      );
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(
          json.message ||
            (json.error ? String(json.error).replace(/_/g, ' ') : 'Could not reset assessment')
        );
        return;
      }
      setMessage(`Assessment retry granted: ${json.label || label}. Student should hard-refresh.`);
      setResetAssessmentTarget((prev) => ({ ...prev, [userId]: '' }));
      await load();
    } catch {
      setError('Network error resetting assessment');
    } finally {
      setResetAssessmentBusy(null);
    }
  };

  const impersonateStudent = async (student) => {
    if (!canImpersonateStudents || !student?.userId || impersonateBusyId) return;
    const label = student.email || student.name || `user ${student.userId}`;
    if (
      !window.confirm(
        `Impersonate ${label}?\n\nYou will see their Learning Center exactly as they do. Use Exit impersonation on the banner to return.`
      )
    ) {
      return;
    }
    setError('');
    setImpersonateBusyId(student.userId);
    try {
      const r = await fetch('/api/admin/impersonate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ userId: student.userId })
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(
          json.message ||
            (json.error ? String(json.error).replace(/_/g, ' ') : 'Could not impersonate student')
        );
        return;
      }
      try {
        setImpersonationEnglishChrome();
      } catch (_) {
        /* ignore */
      }
      setMessage(`Opening as ${json.user?.email || label}…`);
      window.location.href = getLearningDashboardUrl({ openDashboard: true });
    } catch {
      setError('Network error starting student impersonation');
    } finally {
      setImpersonateBusyId(null);
    }
  };

  const previewLms = async () => {
    setLmsBusy(true);
    setError('');
    try {
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/lms-sync`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ dryRun: true, platformKey: 'schoology' })
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json.error ? String(json.error).replace(/_/g, ' ') : 'LMS preview failed');
        return;
      }
      setLmsPreview(json);
      setTab(4);
    } catch {
      setError('Network error previewing LMS sync');
    } finally {
      setLmsBusy(false);
    }
  };

  const pushLms = async () => {
    setLmsBusy(true);
    setError('');
    setMessage('');
    try {
      const r = await fetch(`/api/institutions/active/sections/${sectionId}/lms-sync`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ push: true, dryRun: false, platformKey: 'schoology' })
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok) {
        setError(json.error ? String(json.error).replace(/_/g, ' ') : 'LMS push failed');
        return;
      }
      setLmsPreview(json);
      setMessage(`LMS sync finished: ${json.sent || 0} sent. Students without a graded LMS launch stay listed as not linked.`);
    } catch {
      setError('Network error pushing LMS grades');
    } finally {
      setLmsBusy(false);
    }
  };

  const copyImpact = async () => {
    if (!data?.impactBrief) return;
    try {
      await navigator.clipboard.writeText(data.impactBrief);
      setMessage('Impact brief copied — paste into admin emails or grant narratives.');
    } catch {
      setError('Could not copy to clipboard');
    }
  };

  const reassign = async (studentUserId) => {
    const toSectionId = parseInt(reassignTarget[studentUserId], 10);
    if (!Number.isFinite(toSectionId)) {
      setError('Pick a destination class to reassign.');
      return;
    }
    setError('');
    try {
      const r = await fetch('/api/institutions/active/sections/reassign', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          studentUserId,
          fromSectionId: Number(sectionId),
          toSectionId
        })
      });
      if (!r.ok) {
        setError('Reassign failed');
        return;
      }
      setMessage('Student reassigned.');
      if (typeof onReassigned === 'function') onReassigned();
      await load();
    } catch {
      setError('Network error reassigning');
    }
  };

  const totalSchemePoints = schemeDraft.reduce((a, i) => a + (Number(i.points) || 0), 0);

  return (
    <Paper
      ref={panelRef}
      id="teacher-gradebook-panel"
      sx={{ p: 2, borderRadius: 2, border: '1px solid rgba(34, 211, 238, 0.28)' }}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#e8f4ff', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            Gradebook · {sectionName || 'Class'}
            <HelpTip title="You set the points. CommandLine awards them from verified terminal practice. Use Supports for IEP/504 time and pacing without labeling disabilities." />
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            Proof of practice + teacher-controlled points
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={downloadCsv}
            disabled={disabled || loading}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Download CSV
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyImpact}
            disabled={disabled || !data?.impactBrief}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Copy Impact brief
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={previewLms}
            disabled={disabled || lmsBusy}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            LMS sync preview
          </Button>
        </Stack>
      </Box>

      {error ? (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : null}
      {message ? (
        <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      ) : null}

      {data?.kpis?.length ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1, mb: 1.5 }}>
          {data.kpis.map((k) => {
            const clickable = k.id === 'on_track' || k.id === 'radar' || k.actionable;
            const inner = (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35 }}>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, flex: 1 }}>
                    {k.label}
                  </Typography>
                  {TEACHER_KPI_HELP[k.id] ? (
                    <HelpTip title={TEACHER_KPI_HELP[k.id]} label={`About ${k.label}`} size={14} />
                  ) : null}
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 800, color: k.accent || '#e8f4ff' }}>
                  {k.value}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b' }}>
                  {k.trend}
                </Typography>
              </>
            );
            if (!clickable) {
              return (
                <Paper
                  key={k.id}
                  elevation={0}
                  sx={{ p: 1.25, bgcolor: 'rgba(0,0,0,0.28)', border: '1px solid rgba(120,200,255,0.18)' }}
                >
                  {inner}
                </Paper>
              );
            }
            return (
              <ButtonBase
                key={k.id}
                onClick={() => openKpi(k.id)}
                focusRipple
                sx={{
                  display: 'block',
                  textAlign: 'left',
                  borderRadius: 2,
                  p: 1.25,
                  bgcolor: 'rgba(0,0,0,0.28)',
                  border: '1px solid rgba(120,200,255,0.28)',
                  transition: 'border-color 0.15s, transform 0.15s',
                  '&:hover': { borderColor: k.accent || '#22d3ee', transform: 'translateY(-1px)' }
                }}
              >
                {inner}
              </ButtonBase>
            );
          })}
        </Box>
      ) : null}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1.5, minHeight: 36, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, minHeight: 36 } }}
      >
        <Tab label="Scores" />
        <Tab icon={<RadarIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Intervention Radar" />
        <Tab label="Certificates" />
        <Tab label="Grade scheme" />
        <Tab label="LMS & Impact" />
      </Tabs>

      {loading ? <Typography variant="body2">Loading gradebook…</Typography> : null}

      {!loading && tab === 0 ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              label="All"
              onClick={() => setScoreFilter('all')}
              color={scoreFilter === 'all' ? 'primary' : 'default'}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              label="On track"
              onClick={() => setScoreFilter('on_track')}
              color={scoreFilter === 'on_track' ? 'primary' : 'default'}
              sx={{ fontWeight: 700 }}
            />
            <Chip
              size="small"
              label="Below bar"
              onClick={() => setScoreFilter('behind')}
              color={scoreFilter === 'behind' ? 'primary' : 'default'}
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Student</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Level</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Pathway</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Scheme</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Override</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Final</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>
                  Supports
                  <HelpTip
                    title="IEP/504-style actions: extended time (150%/200%), untimed, extra attempts, alternate pacing. Stored as supports — not disability labels."
                    sx={{ ml: 0.25 }}
                  />
                </TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Risk</TableCell>
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>
                  Assessment retry
                  <HelpTip
                    title="Clear a passed assessment so the student can retake: Linux Phase 1–3, Python 101/201/301, SQL 201/301, Ethics checkpoints/capstone, Cloud Security 101. Does not reset track certifications."
                    sx={{ ml: 0.25 }}
                  />
                </TableCell>
                {canImpersonateStudents ? (
                  <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>
                    Impersonate
                    <HelpTip
                      title="Owner support only: open this student’s Learning Center as them. Available when you are the owner, or after you impersonated their teacher."
                      sx={{ ml: 0.25 }}
                    />
                  </TableCell>
                ) : null}
                <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Reassign</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStudents.map((s) => {
                const risk = riskChip(s.riskLevel);
                const supports = s.accommodation?.hasSupports;
                const resetCatalog = data?.resettableAssessments || [];
                const passedAssessments = resetCatalog.filter(
                  (a) => s.assessmentPasses && s.assessmentPasses[a.level]
                );
                const hasAnyPassed = passedAssessments.length > 0;
                return (
                  <TableRow key={s.userId}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        className="skylite-privacy-blur"
                        data-skylite-privacy="blur"
                        sx={{ fontWeight: 700, color: '#e8f4ff' }}
                      >
                        {s.name}
                      </Typography>
                      <Typography
                        variant="caption"
                        className="notranslate skylite-privacy-blur"
                        translate="no"
                        data-skylite-privacy="blur"
                        sx={{ color: '#64748b' }}
                      >
                        {s.email}
                      </Typography>
                    </TableCell>
                    <TableCell className="notranslate" translate="no">
                      {s.currentLesson != null ? s.currentLesson : '—'}
                    </TableCell>
                    <TableCell>{s.pathwayPct}%</TableCell>
                    <TableCell className="notranslate" translate="no">
                      {s.earned}/{s.maxPoints}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          size="small"
                          value={overrideDrafts[s.userId] ?? ''}
                          onChange={(e) =>
                            setOverrideDrafts((prev) => ({ ...prev, [s.userId]: e.target.value }))
                          }
                          placeholder="auto"
                          sx={{ width: 72 }}
                          inputProps={{ className: 'notranslate', inputMode: 'decimal' }}
                        />
                        <Button size="small" onClick={() => saveOverride(s.userId)} sx={{ textTransform: 'none', minWidth: 0 }}>
                          Save
                        </Button>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>
                      {s.finalPct}%
                      <Typography variant="caption" sx={{ display: 'block', color: '#64748b' }}>
                        bar {s.onTrackPctThreshold ?? data?.scheme?.onTrackPct ?? 80}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant={supports ? 'contained' : 'outlined'}
                        onClick={() => setSupportsStudent(s)}
                        disabled={disabled}
                        sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}
                      >
                        {supports ? 'Supports on' : 'Add supports'}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={risk.label}
                        sx={{ height: 22, fontWeight: 700, color: risk.color, bgcolor: risk.bg }}
                      />
                    </TableCell>
                    <TableCell>
                      {resetCatalog.length ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <TextField
                            select
                            size="small"
                            SelectProps={{ native: true }}
                            value={resetAssessmentTarget[s.userId] || ''}
                            onChange={(e) =>
                              setResetAssessmentTarget((prev) => ({
                                ...prev,
                                [s.userId]: e.target.value
                              }))
                            }
                            disabled={disabled || resetAssessmentBusy === s.userId || !hasAnyPassed}
                            sx={{ minWidth: 180 }}
                          >
                            <option value="">
                              {hasAnyPassed ? 'Retake…' : 'None passed yet'}
                            </option>
                            {resetCatalog.map((a) => {
                              const passed = !!(s.assessmentPasses && s.assessmentPasses[a.level]);
                              return (
                                <option
                                  key={a.level}
                                  value={String(a.level)}
                                  disabled={!passed}
                                >
                                  {passed ? a.label : `${a.label} (not passed)`}
                                </option>
                              );
                            })}
                          </TextField>
                          <Button
                            size="small"
                            onClick={() => resetAssessment(s.userId)}
                            disabled={
                              disabled ||
                              resetAssessmentBusy === s.userId ||
                              !resetAssessmentTarget[s.userId]
                            }
                            sx={{ textTransform: 'none', minWidth: 0, fontWeight: 700 }}
                          >
                            {resetAssessmentBusy === s.userId ? '…' : 'Allow'}
                          </Button>
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                    {canImpersonateStudents ? (
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => impersonateStudent(s)}
                          disabled={disabled || impersonateBusyId === s.userId}
                          sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12 }}
                        >
                          {impersonateBusyId === s.userId ? '…' : 'As student'}
                        </Button>
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <TextField
                          select
                          size="small"
                          SelectProps={{ native: true }}
                          value={reassignTarget[s.userId] || ''}
                          onChange={(e) =>
                            setReassignTarget((prev) => ({ ...prev, [s.userId]: e.target.value }))
                          }
                          sx={{ minWidth: 120 }}
                        >
                          <option value="">Move to…</option>
                          {sections
                            .filter((sec) => String(sec.id) !== String(sectionId))
                            .map((sec) => (
                              <option key={sec.id} value={String(sec.id)}>
                                {sec.name}
                              </option>
                            ))}
                        </TextField>
                        <Button size="small" onClick={() => reassign(s.userId)} sx={{ textTransform: 'none', minWidth: 0 }}>
                          Go
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!filteredStudents.length ? (
                <TableRow>
                  <TableCell colSpan={canImpersonateStudents ? 11 : 10}>
                    <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                      {scoreFilter === 'all'
                        ? 'No students in this class yet. Add by email, pool, or join code.'
                        : 'No students match this filter for the selected class.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </Box>
      ) : null}

      {!loading && tab === 1 ? (
        <Box>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
            <span>
              Students ranked by struggle signals: inactivity, behind class median, or low mastery. Use this Monday
              morning — not a vanity chart.
            </span>
            <HelpTip title="Flags respect class bars and each student’s Supports (longer idle grace, skip median compare, personal mastery bars)." />
          </Typography>
          {(data?.radar || []).length === 0 ? (
            <Typography variant="body2" sx={{ color: '#6ee7b7' }}>
              No students currently flagged. Keep an eye on Scores as the class grows.
            </Typography>
          ) : (
            (data.radar || []).map((s) => {
              const risk = riskChip(s.riskLevel);
              return (
                <Paper
                  key={s.userId}
                  elevation={0}
                  sx={{ p: 1.5, mb: 1, bgcolor: 'rgba(0,0,0,0.28)', border: '1px solid rgba(251,146,60,0.25)' }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      className="skylite-privacy-blur"
                      data-skylite-privacy="blur"
                      sx={{ fontWeight: 800, color: '#e8f4ff' }}
                    >
                      {s.name}
                    </Typography>
                    <Chip size="small" label={risk.label} sx={{ color: risk.color, bgcolor: risk.bg, fontWeight: 700 }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 0.5 }}>
                    Final {s.finalPct}% · Pathway {s.pathwayPct}%
                    {s.idleDays != null ? ` · Idle ${s.idleDays}d` : ''}
                  </Typography>
                  <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#cbd5e1', fontSize: 13 }}>
                    {(s.riskReasons || []).map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                  <Typography variant="caption" sx={{ color: '#67e8f9', display: 'block', mt: 0.75 }}>
                    Suggested: check in this week, reopen the last unfinished level slides, or pair with a peer who is on
                    track.
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1, textTransform: 'none', fontWeight: 700 }}
                    onClick={() => setSupportsStudent(s)}
                    disabled={disabled}
                  >
                    {s.accommodation?.hasSupports ? 'Edit supports (IEP/504)' : 'Add supports (IEP/504)'}
                  </Button>
                </Paper>
              );
            })
          )}
        </Box>
      ) : null}

      {!loading && tab === 2 ? (
        <Box>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5, lineHeight: 1.65 }}>
            CommandLine achievement certificates (not CompTIA). Student downloads leave the name blank for write-in;
            teacher/admin downloads print the account name. Ceremony prints from here stay blank for handwriting.
            Each PDF includes the CEO signature. Export CSV for class records.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={downloadCertificatesCsv}
              disabled={disabled || loading}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Download certificates CSV
            </Button>
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.75 }}>
            Print certifications
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            {(data?.certificateCatalog || []).map((c) => (
              <Button
                key={c.id}
                size="small"
                variant="outlined"
                onClick={() => printBlankCertificate(c)}
                disabled={disabled}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                {c.title}
              </Button>
            ))}
          </Stack>

          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.75 }}>
            Earners in this class
          </Typography>
          {certificateEarners.length === 0 ? (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              No certificates earned yet. They appear after students pass phase assessments or track certs.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Student</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Certificate</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Level</TableCell>
                    <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>Print</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {certificateEarners.map((row) => (
                    <TableRow key={`${row.userId}-${row.id}`}>
                      <TableCell>
                        <Typography
                          variant="body2"
                          className="skylite-privacy-blur"
                          data-skylite-privacy="blur"
                          sx={{ fontWeight: 700, color: '#e8f4ff' }}
                        >
                          {row.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          className="notranslate skylite-privacy-blur"
                          translate="no"
                          data-skylite-privacy="blur"
                          sx={{ color: '#64748b' }}
                        >
                          {row.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ color: '#e8f4ff' }}>
                          {row.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          {row.subtitle}
                        </Typography>
                      </TableCell>
                      <TableCell className="notranslate" translate="no">
                        {row.level}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          onClick={() => printBlankCertificate(row)}
                          sx={{ textTransform: 'none', fontWeight: 700, minWidth: 0 }}
                        >
                          Certification
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>
      ) : null}

      {!loading && tab === 3 ? (
        <Box>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5 }}>
            You set the points and class bars. CommandLine awards points when students complete verified level milestones.
            Per-student IEP/504 supports can override bars without changing the whole class.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              type="number"
              label="On-track %"
              value={onTrackPct}
              onChange={(e) => setOnTrackPct(e.target.value)}
              sx={{ width: 120 }}
              inputProps={{ min: 0, max: 100 }}
            />
            <HelpTip title="Class default: students at or above this gradebook % count as On track. Individual Supports can set a personal bar." />
            <TextField
              size="small"
              type="number"
              label="Low mastery %"
              value={interventionMasteryPct}
              onChange={(e) => setInterventionMasteryPct(e.target.value)}
              sx={{ width: 130 }}
              inputProps={{ min: 0, max: 100 }}
            />
            <HelpTip title="Pathway mastery below this % is an intervention signal (unless a student’s Supports set a different bar)." />
            <TextField
              size="small"
              type="number"
              label="Idle days"
              value={idleDaysThreshold}
              onChange={(e) => setIdleDaysThreshold(e.target.value)}
              sx={{ width: 110 }}
              inputProps={{ min: 1, max: 60 }}
            />
            <HelpTip title="No practice for this many days flags idle. Alternate pacing doubles this; Supports can set a custom grace." />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              label="Scheme name"
              value={schemeName}
              onChange={(e) => setSchemeName(e.target.value)}
              fullWidth
            />
            <Button
              variant="outlined"
              onClick={() => saveScheme(true)}
              disabled={savingScheme || disabled}
              sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Reset Linux Phase 1
            </Button>
            <Button
              variant="contained"
              onClick={() => saveScheme(false)}
              disabled={savingScheme || disabled}
              sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              Save scheme ({totalSchemePoints} pts)
            </Button>
          </Stack>
          {schemeDraft.map((it, idx) => (
            <Stack
              key={it.itemKey || idx}
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <TextField
                size="small"
                label="Label"
                value={it.label}
                onChange={(e) => {
                  const next = [...schemeDraft];
                  next[idx] = { ...next[idx], label: e.target.value };
                  setSchemeDraft(next);
                }}
                fullWidth
              />
              <TextField
                size="small"
                label="Points"
                type="number"
                value={it.points}
                onChange={(e) => {
                  const next = [...schemeDraft];
                  next[idx] = { ...next[idx], points: e.target.value };
                  setSchemeDraft(next);
                }}
                sx={{ width: 100 }}
                inputProps={{ className: 'notranslate', min: 0, max: 1000 }}
              />
            </Stack>
          ))}
        </Box>
      ) : null}

      {!loading && tab === 4 ? (
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.75 }}>
            LMS sync (teacher-controlled)
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1.5, lineHeight: 1.65 }}>
            Preview first, then push. Scores go only to students who launched CommandLine from a <strong>graded</strong>{' '}
            Schoology/Canvas assignment (LTI AGS). Everyone else still has CSV — no silent grade spam.
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={previewLms}
              disabled={lmsBusy || disabled}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Refresh preview
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={pushLms}
              disabled={lmsBusy || disabled || !lmsPreview}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              Push grades to LMS
            </Button>
          </Stack>
          {lmsPreview ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
                Ready: {lmsPreview.ready ?? lmsPreview.sent ?? 0}
                {lmsPreview.blocked != null ? ` · Not linked: ${lmsPreview.blocked}` : ''}
              </Typography>
              <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                {(lmsPreview.students || []).map((row) => (
                  <Box
                    key={row.userId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 1,
                      py: 0.5,
                      borderBottom: '1px solid rgba(148,163,184,0.15)'
                    }}
                  >
                    <Typography
                      variant="caption"
                      className="skylite-privacy-blur"
                      data-skylite-privacy="blur"
                      sx={{ color: '#e8f4ff' }}
                    >
                      {row.name}
                    </Typography>
                    <Typography variant="caption" className="notranslate" translate="no" sx={{ color: '#94a3b8' }}>
                      {row.scoreGiven}/{row.scoreMaximum} · {row.status}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ) : null}

          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#e8f4ff', mb: 0.75 }}>
            Impact brief (admins & grants)
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              bgcolor: 'rgba(0,0,0,0.35)',
              border: '1px solid rgba(13,148,136,0.35)',
              whiteSpace: 'pre-wrap',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
              fontSize: 12,
              color: '#cbd5e1',
              lineHeight: 1.55
            }}
          >
            {data?.impactBrief || 'Load a class with students to generate an impact brief.'}
          </Paper>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={copyImpact}
            disabled={!data?.impactBrief}
            sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}
          >
            Copy Impact brief
          </Button>
        </Box>
      ) : null}

      <StudentAccommodationDialog
        open={Boolean(supportsStudent)}
        onClose={() => setSupportsStudent(null)}
        student={supportsStudent}
        sectionId={sectionId}
        disabled={disabled}
        onSaved={async (userId, accommodation) => {
          setMessage(
            accommodation?.hasSupports
              ? 'Assessment supports saved. Intervention math will respect this student’s plan.'
              : 'Assessment supports cleared.'
          );
          setSupportsStudent(null);
          await load();
        }}
      />
    </Paper>
  );
}
